/**
 * AuditSecurityPage - "Journal d'audit & sécurité"
 * Page premium d'audit & sécurité : KPIs, bandeau d'alerte, onglets, filtres
 * avancés, tableau aéré et drawer de détail (contexte technique + actions).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import {
  Activity, Shield, Lock, AlertTriangle, Users, MapPin, Database, Server,
  Braces, FileText, RefreshCw, Search, Filter, Eye, MoreHorizontal, X,
  CheckCircle, XCircle, Copy, Download, ShieldAlert, ChevronLeft, ChevronRight,
  CircleDot, ChevronDown, Inbox,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import { toast } from '../../hooks/use-toast';
import LoginJournalTab from './LoginJournalTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

// ---- Métadonnées visuelles -------------------------------------------------
const SEVERITY_META = {
  info: { label: 'Info', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: '#3b82f6' },
  faible: { label: 'Faible', cls: 'bg-slate-50 text-slate-600 border-slate-200', dot: '#64748b' },
  moyen: { label: 'Moyen', cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: '#2563eb' },
  eleve: { label: 'Élevé', cls: 'bg-orange-50 text-orange-700 border-orange-200', dot: '#f97316' },
  critique: { label: 'Critique', cls: 'bg-red-50 text-red-700 border-red-200', dot: '#ef4444' },
};
const OUTCOME_META = {
  success: { label: 'Succès', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  failure: { label: 'Échec', cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  unknown: { label: 'Inconnu', cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: CircleDot },
};
const TABS = [
  { value: 'all', label: 'Tous', stat: 'total' },
  { value: 'security', label: 'Sécurité', stat: 'security' },
  { value: 'crud', label: 'Opérations', stat: 'crud' },
  { value: 'rbac', label: 'RBAC', stat: 'rbac' },
  { value: 'backup', label: 'Backup & restauration', stat: 'backup' },
  { value: 'modification', label: 'Modifications', stat: 'modification' },
  { value: 'finance', label: 'Finance', stat: 'finance' },
  { value: 'fne', label: 'FNE', stat: 'fne' },
  { value: 'notification', label: 'Notifications', stat: 'notification' },
  { value: 'whatsapp', label: 'WhatsApp', stat: 'whatsapp' },
  { value: 'sensitive', label: 'Actions sensibles', stat: 'sensitive', special: true },
  { value: 'connexions', label: 'Connexions', special: true, noCount: true },
];

// ---- Helpers ---------------------------------------------------------------
const initials = (s = '') => {
  const base = String(s).split('@')[0].replace(/[._-]/g, ' ').trim();
  const parts = base.split(' ').filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || (base[0] || '?').toUpperCase();
};
const formatRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  return `Il y a ${Math.floor(diff / 86400)} j`;
};
const formatExact = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const resourceLabel = (r) => {
  if (!r) return '—';
  if (Array.isArray(r)) return r.join(', ');
  if (typeof r === 'object') return Object.values(r).filter(Boolean).join(', ');
  return String(r);
};

// ---- Petits composants -----------------------------------------------------
const Sparkline = ({ data = [], color = '#10b981' }) => {
  if (!data || data.length === 0) return <div className="h-9 w-full" aria-hidden />;
  return (
    <div className="h-9 w-full" aria-hidden>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <LineChart data={data.map((v, i) => ({ i, v }))} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const KpiCard = ({ icon: Icon, title, value, subtext, accent = 'text-gray-400', valueClass = 'text-gray-900', spark, sparkColor, testid }) => (
  <div data-testid={testid} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col justify-between">
    <div className="flex items-start justify-between">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className={`text-2xl font-bold mt-2 ${valueClass}`}>{value}</div>
    <div className="flex items-end justify-between gap-2 mt-1">
      <span className="text-xs text-gray-500">{subtext}</span>
      {spark && <div className="w-20"><Sparkline data={spark} color={sparkColor} /></div>}
    </div>
  </div>
);

const SeverityBadge = ({ level }) => {
  const m = SEVERITY_META[level] || SEVERITY_META.info;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${m.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} />{m.label}
    </span>
  );
};
const OutcomeBadge = ({ outcome }) => {
  const m = OUTCOME_META[outcome] || OUTCOME_META.unknown;
  const I = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${m.cls}`}>
      <I className="w-3 h-3" />{m.label}
    </span>
  );
};

// ============================================================================
const AuditSecurityPage = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [uniqueIps, setUniqueIps] = useState([]);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [incidentMode, setIncidentMode] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  const [filters, setFilters] = useState({
    tab: 'all', severity: 'all', outcome: 'all', user: '', ip: 'all',
    search: '', dateFrom: '', dateTo: '', requiresReviewOnly: false, includeReads: false,
  });

  const buildParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (filters.tab === 'sensitive') p.append('sensitive_only', 'true');
    else if (filters.tab !== 'all') p.append('category', filters.tab);
    if (filters.severity !== 'all') p.append('severity', filters.severity);
    if (filters.outcome !== 'all') p.append('outcome', filters.outcome);
    if (filters.user) p.append('user', filters.user);
    if (filters.ip !== 'all') p.append('ip', filters.ip);
    if (filters.search) p.append('search', filters.search);
    if (filters.dateFrom) p.append('date_from', filters.dateFrom);
    if (filters.dateTo) p.append('date_to', filters.dateTo);
    if (filters.requiresReviewOnly) p.append('requires_review_only', 'true');
    if (filters.includeReads) p.append('include_reads', 'true');
    Object.entries(extra).forEach(([k, v]) => p.append(k, v));
    return p;
  }, [filters]);

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = buildParams({ limit: PAGE_SIZE, offset });
      const res = await fetch(`${API}/logs/unified?${p}`, {});
      if (!res.ok) throw new Error('fetch');
      const data = await res.json();
      setEntries(data.entries || []);
      setStats(data.stats || {});
      setTotal(data.total || 0);
      setUniqueIps(data.uniqueIps || []);
      setLastUpdate(new Date());
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [buildParams, offset]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  // Reset page on filter change
  useEffect(() => { setOffset(0); }, [filters.tab, filters.severity, filters.outcome, filters.user, filters.ip, filters.search, filters.dateFrom, filters.dateTo, filters.requiresReviewOnly, filters.includeReads]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => {
    setFilters({ tab: 'all', severity: 'all', outcome: 'all', user: '', ip: 'all', search: '', dateFrom: '', dateTo: '', requiresReviewOnly: false, includeReads: false });
    setSearchInput('');
  };
  const applySearch = () => setFilter('search', searchInput.trim());

  const doExport = async (format) => {
    try {
      const p = buildParams({ format });
      const res = await fetch(`${API}/logs/export?${p}`, {});
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `logs-${new Date().toISOString().slice(0, 10)}.${format === 'csv' ? 'csv' : 'json'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: 'Export réussi', description: `Fichier ${format.toUpperCase()} téléchargé` });
    } catch {
      toast({ title: 'Erreur', description: "Échec de l'export", variant: 'destructive' });
    }
  };

  const verifyEvents = async (eventIds) => {
    try {
      const res = await fetch(`${API}/logs/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_ids: eventIds }),
      });
      if (!res.ok) throw new Error();
      toast({ title: 'Vérifié', description: `${eventIds.length} événement(s) marqué(s) comme vérifié(s)` });
      fetchLogs();
      if (selected && eventIds.includes(selected.event_id)) {
        setSelected((s) => ({ ...s, review_status: 'verified', requires_review: false }));
      }
    } catch {
      toast({ title: 'Erreur', description: 'Échec de la vérification', variant: 'destructive' });
    }
  };

  const createIncident = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API}/logs/incident`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: selected.event_id,
          title: incidentTitle || `Incident — ${selected.action}`,
          description: selected.message || '',
          severity: selected.severity === 'critique' ? 'haute' : 'moyenne',
          snapshot: selected,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast({ title: 'Incident créé', description: `Référence ${data.incident_id}` });
      setIncidentMode(false); setIncidentTitle('');
      setSelected((s) => ({ ...s, review_status: 'verified', incident_id: data.incident_id }));
      fetchLogs();
    } catch {
      toast({ title: 'Erreur', description: "Échec de la création d'incident", variant: 'destructive' });
    }
  };

  const copyEvent = (entry) => {
    navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
    toast({ title: 'Copié', description: "Événement copié dans le presse-papiers" });
  };
  const downloadEvent = (entry) => {
    const blob = new Blob([JSON.stringify(entry, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${entry.event_id?.replace(/[:/]/g, '_') || 'event'}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const openDrawer = (entry) => { setSelected(entry); setShowJson(false); setIncidentMode(false); setIncidentTitle(''); };

  const pendingSensitiveIds = useMemo(() => entries.filter((e) => e.requires_review).map((e) => e.event_id), [entries]);
  const hasMore = offset + entries.length < total;

  // ---- Render --------------------------------------------------------------
  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px] mx-auto" data-testid="audit-security-page">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Journal d'audit &amp; sécurité</h1>
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs px-2 py-1 rounded-full font-medium">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />En ligne
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">Suivez les actions sensibles, les accès, les restaurations, les exports et les événements système.</p>
        </div>
        <div className="flex flex-col items-start lg:items-end gap-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="btn-export-json" onClick={() => doExport('json')}><Braces className="w-4 h-4 mr-1.5" />Exporter JSON</Button>
            <Button variant="outline" size="sm" data-testid="btn-export-csv" onClick={() => doExport('csv')}><FileText className="w-4 h-4 mr-1.5" />Exporter CSV</Button>
            <Button variant="outline" size="sm" data-testid="btn-export-ecs" onClick={() => doExport('ecs')}><Server className="w-4 h-4 mr-1.5" />Export SIEM</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-refresh" onClick={fetchLogs}><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Actualiser</Button>
          </div>
          <div className="text-xs text-gray-500 flex flex-wrap items-center gap-3">
            <span>Dernière mise à jour : {lastUpdate ? formatRelative(lastUpdate.toISOString()) : '—'}</span>
            <span className="text-gray-300">•</span>
            <span>Rétention : 90 jours</span>
            <span className="text-gray-300">•</span>
            <span className="text-emerald-600 font-medium">Standards ECS / RFC 5424</span>
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {loading && !entries.length ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)
        ) : (
          <>
            <KpiCard testid="kpi-total" icon={Activity} title="Total événements" value={(stats.total || 0).toLocaleString('fr-FR')} subtext="Logs indexés" accent="text-emerald-500" spark={stats.sparklines?.total} sparkColor="#10b981" />
            <KpiCard testid="kpi-today" icon={Activity} title="Aujourd'hui" value={(stats.today || 0).toLocaleString('fr-FR')} subtext="Sur les dernières 24h" accent="text-blue-500" spark={stats.sparklines?.total} sparkColor="#3b82f6" />
            <KpiCard testid="kpi-sensitive" icon={AlertTriangle} title="Actions sensibles" value={(stats.sensitive || 0).toLocaleString('fr-FR')} subtext={`${stats.requiresReview || 0} à vérifier`} accent="text-orange-500" valueClass="text-orange-600" spark={stats.sparklines?.sensitive} sparkColor="#f97316" />
            <KpiCard testid="kpi-security" icon={Shield} title="Événements sécurité" value={(stats.security || 0).toLocaleString('fr-FR')} subtext={`${stats.failures || 0} échecs`} accent="text-emerald-500" spark={stats.sparklines?.security} sparkColor="#10b981" />
            <KpiCard testid="kpi-users" icon={Users} title="Utilisateurs concernés" value={(stats.uniqueUsers || 0).toLocaleString('fr-FR')} subtext="Comptes dans les logs" accent="text-purple-500" />
            <KpiCard testid="kpi-ips" icon={MapPin} title="IPs uniques" value={(stats.uniqueIps || 0).toLocaleString('fr-FR')} subtext="Sources détectées" accent="text-blue-500" />
          </>
        )}
      </div>

      {/* ALERT BANNER */}
      {!bannerDismissed && (stats.requiresReview || 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="alert-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <span className="font-semibold">{stats.requiresReview} action(s) sensible(s)</span> nécessitent une vérification manuelle.
              <span className="block text-amber-700/80">Vérifiez que ces actions sont conformes aux procédures internes.</span>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="bg-white border-amber-200 text-amber-900 hover:bg-amber-100" data-testid="banner-view-sensitive" onClick={() => setFilter('tab', 'sensitive')}>Voir les événements sensibles</Button>
            {pendingSensitiveIds.length > 0 && (
              <Button size="sm" variant="ghost" className="text-amber-800" data-testid="banner-verify-all" onClick={() => verifyEvents(pendingSensitiveIds)}>Marquer la page comme vérifiée</Button>
            )}
            <Button size="icon" variant="ghost" className="text-amber-700 h-8 w-8" data-testid="banner-dismiss" onClick={() => setBannerDismissed(true)}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="w-full overflow-x-auto border-b border-gray-200">
        <div className="flex gap-6 min-w-max">
          {TABS.map((t) => {
            const active = filters.tab === t.value;
            const count = stats[t.stat] ?? 0;
            return (
              <button key={t.value} data-testid={`tab-${t.value}`} onClick={() => setFilter('tab', t.value)}
                className={`pb-2.5 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${active ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {t.label}
                {!t.noCount && (
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{count.toLocaleString('fr-FR')}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* SEARCH + FILTERS */}
      {filters.tab === 'connexions' ? <LoginJournalTab /> : (
      <>
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="Rechercher utilisateur, IP, action, ressource, détails…" className="pl-9" />
          </div>
          <Button variant="outline" size="sm" data-testid="btn-search" onClick={applySearch}>Rechercher</Button>
          <Button variant="outline" size="sm" data-testid="btn-toggle-filters" onClick={() => setShowFilters((s) => !s)}>
            <Filter className="w-4 h-4 mr-1.5" />Filtres avancés <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-2 items-center bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="filters-panel">
            <Select value={filters.severity} onValueChange={(v) => setFilter('severity', v)}>
              <SelectTrigger className="w-[150px] bg-white" data-testid="filter-severity"><SelectValue placeholder="Criticité" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes criticités</SelectItem>
                {Object.entries(SEVERITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.outcome} onValueChange={(v) => setFilter('outcome', v)}>
              <SelectTrigger className="w-[140px] bg-white" data-testid="filter-outcome"><SelectValue placeholder="Résultat" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous résultats</SelectItem>
                <SelectItem value="success">Succès</SelectItem>
                <SelectItem value="failure">Échec</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.ip} onValueChange={(v) => setFilter('ip', v)}>
              <SelectTrigger className="w-[160px] bg-white" data-testid="filter-ip"><SelectValue placeholder="Adresse IP" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Toutes les IP</SelectItem>
                {uniqueIps.map((ip) => <SelectItem key={ip} value={ip}>{ip}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="filter-user" value={filters.user} onChange={(e) => setFilter('user', e.target.value)} placeholder="Utilisateur…" className="w-[180px] bg-white" />
            <Input data-testid="filter-date-from" type="date" value={filters.dateFrom} onChange={(e) => setFilter('dateFrom', e.target.value)} className="w-[150px] bg-white" />
            <Input data-testid="filter-date-to" type="date" value={filters.dateTo} onChange={(e) => setFilter('dateTo', e.target.value)} className="w-[150px] bg-white" />
            <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="filter-requires-review">
              <Switch checked={filters.requiresReviewOnly} onCheckedChange={(v) => setFilter('requiresReviewOnly', v)} />À vérifier uniquement
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="filter-include-reads">
              <Switch checked={filters.includeReads} onCheckedChange={(v) => setFilter('includeReads', v)} />Inclure les consultations
            </label>
            <Button variant="ghost" size="sm" data-testid="btn-clear-filters" onClick={clearFilters}>Réinitialiser</Button>
          </div>
        )}
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Date / Heure', 'Criticité', 'Événement', 'Utilisateur', 'Ressource', 'IP', 'Résultat', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100"><td colSpan={8} className="px-4 py-3"><Skeleton className="h-8 w-full" /></td></tr>
                ))
              ) : error ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <p className="text-gray-600 mb-3">Une erreur est survenue lors du chargement des journaux.</p>
                  <Button variant="outline" onClick={fetchLogs} data-testid="btn-retry"><RefreshCw className="w-4 h-4 mr-1.5" />Réessayer</Button>
                </td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center" data-testid="empty-state">
                  <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Aucun événement trouvé</p>
                  <p className="text-gray-500 text-sm mb-3">Essayez d'élargir la période ou de modifier vos filtres.</p>
                  <Button variant="outline" size="sm" onClick={clearFilters}>Réinitialiser les filtres</Button>
                </td></tr>
              ) : (
                entries.map((e) => (
                  <tr key={e.event_id} data-testid="log-table-row" onClick={() => openDrawer(e)}
                    className="border-b border-gray-100 hover:bg-gray-50/70 cursor-pointer transition-colors group">
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm text-gray-900">{formatRelative(e.timestamp)}</div>
                      <div className="text-xs text-gray-500">{formatExact(e.timestamp)}</div>
                    </td>
                    <td className="px-4 py-3 align-top"><SeverityBadge level={e.severity} /></td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        {e.sensitive && <ShieldAlert className="w-3.5 h-3.5 text-orange-500" title="Action sensible" />}
                        {e.message || e.action}
                      </div>
                      <div className="text-xs text-gray-500">{e.module}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center shrink-0">{initials(e.user)}</span>
                        <div>
                          <div className="text-sm text-gray-900 truncate max-w-[160px]">{e.user}</div>
                          {e.user_role && <div className="text-xs text-gray-500">{e.user_role}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="font-mono text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{resourceLabel(e.resource) || e.endpoint || '—'}</span>
                    </td>
                    <td className="px-4 py-3 align-top font-mono text-sm text-gray-500">{e.ip}</td>
                    <td className="px-4 py-3 align-top"><OutcomeBadge outcome={e.outcome} /></td>
                    <td className="px-4 py-3 align-top text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="row-view" onClick={(ev) => { ev.stopPropagation(); openDrawer(e); }}>
                        <Eye className="w-4 h-4 text-gray-500" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* PAGINATION */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 text-sm text-gray-500">
          <span data-testid="pagination-info">Affichage {total === 0 ? 0 : offset + 1} à {offset + entries.length} sur {total.toLocaleString('fr-FR')} événements</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} data-testid="page-prev" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-gray-700">Page {Math.floor(offset / PAGE_SIZE) + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
            <Button variant="outline" size="sm" disabled={!hasMore} data-testid="page-next" onClick={() => setOffset(offset + PAGE_SIZE)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
      </>
      )}

      {/* DRAWER */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto p-0" data-testid="event-drawer">
          {selected && (
            <div className="flex flex-col h-full">
              <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg font-semibold text-gray-900">Détail de l'événement</SheetTitle>
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="drawer-close" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button>
                </div>
                <SheetDescription className="sr-only">Détail technique de l'événement d'audit {selected.event_id}</SheetDescription>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="bg-gray-50">{selected.module}</Badge>
                  <SeverityBadge level={selected.severity} />
                  <OutcomeBadge outcome={selected.outcome} />
                  {selected.review_status === 'verified' && <Badge className="bg-emerald-100 text-emerald-700 border-0">Vérifié</Badge>}
                  {selected.incident_id && <Badge className="bg-red-100 text-red-700 border-0">{selected.incident_id}</Badge>}
                </div>
              </div>

              <div className="px-6 py-5 space-y-6 flex-1">
                {/* Infos générales */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Informations générales</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <Info label="Date / Heure" value={formatExact(selected.timestamp)} />
                    <Info label="Utilisateur" value={selected.user} />
                    <Info label="Rôle" value={selected.user_role || '—'} />
                    <Info label="Adresse IP" value={selected.ip} mono />
                    <Info label="Résultat" value={(OUTCOME_META[selected.outcome] || OUTCOME_META.unknown).label} />
                    <Info label="Module" value={selected.module} />
                    <Info label="Source" value={selected.source_label} />
                    <Info label="Action sensible" value={selected.sensitive ? 'Oui' : 'Non'} />
                    <Info label="À vérifier" value={selected.requires_review ? 'Oui' : 'Non'} />
                  </div>
                </section>

                {/* Détails */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Détails</h3>
                  <div className="bg-gray-50 border border-gray-100 p-4 rounded-lg text-sm text-gray-700">{selected.message || selected.action}</div>
                </section>

                {/* Contexte technique */}
                <section>
                  <button className="flex items-center justify-between w-full text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-testid="toggle-tech" onClick={() => setShowJson((s) => !s)}>
                    <span>Contexte technique</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showJson ? 'rotate-180' : ''}`} />
                  </button>
                  <div className="grid grid-cols-1 gap-2 text-sm mb-3">
                    <Info label="Event ID" value={selected.event_id} mono />
                    <Info label="Request ID" value={selected.request_id || '—'} mono />
                    <Info label="Trace ID" value={selected.trace_id || '—'} mono />
                    <Info label="User Agent" value={selected.user_agent || '—'} />
                  </div>
                  {showJson && (
                    <pre className="bg-gray-900 text-emerald-400 font-mono text-xs p-4 rounded-lg overflow-x-auto max-h-72" data-testid="json-viewer">
{JSON.stringify(selected, null, 2)}
                    </pre>
                  )}
                </section>

                {/* Incident form */}
                {incidentMode && (
                  <section className="border border-orange-200 bg-orange-50 rounded-lg p-4 space-y-2" data-testid="incident-form">
                    <h3 className="text-sm font-semibold text-orange-800">Créer un incident de sécurité</h3>
                    <Input value={incidentTitle} onChange={(e) => setIncidentTitle(e.target.value)} placeholder="Titre de l'incident" className="bg-white" data-testid="incident-title" />
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="incident-submit" onClick={createIncident}>Créer l'incident</Button>
                      <Button size="sm" variant="ghost" onClick={() => setIncidentMode(false)}>Annuler</Button>
                    </div>
                  </section>
                )}
              </div>

              {/* Footer actions */}
              <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="drawer-copy" onClick={() => copyEvent(selected)}><Copy className="w-4 h-4 mr-1.5" />Copier</Button>
                <Button size="sm" variant="outline" data-testid="drawer-download" onClick={() => downloadEvent(selected)}><Download className="w-4 h-4 mr-1.5" />JSON</Button>
                {selected.review_status !== 'verified' && (
                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" data-testid="drawer-verify" onClick={() => verifyEvents([selected.event_id])}><CheckCircle className="w-4 h-4 mr-1.5" />Marquer vérifié</Button>
                )}
                <Button size="sm" variant="outline" className="text-orange-700 border-orange-200" data-testid="drawer-create-incident" onClick={() => setIncidentMode(true)}><ShieldAlert className="w-4 h-4 mr-1.5" />Créer un incident</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid="drawer-more"><MoreHorizontal className="w-4 h-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid="drawer-view-user" onClick={() => { setFilter('user', selected.user); setSelected(null); }}>Voir tous les événements de cet utilisateur</DropdownMenuItem>
                    {selected.ip && selected.ip !== '-' && (
                      <DropdownMenuItem data-testid="drawer-view-ip" onClick={() => { setShowFilters(true); setFilter('ip', selected.ip); setSelected(null); }}>Voir tous les événements de cette IP</DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Info = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-sm text-gray-900 font-medium break-words ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);

export default AuditSecurityPage;
